import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header21 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template21' },
        { label: 'Collections', path: '/template21/collections' },
        { label: 'Shop', path: '/template21/shop' },
        { label: 'About', path: '/template21/about' },
        { label: 'Contact', path: '/template21/contact' },
        { label: 'Orders', path: '/template21/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? '#0A0A0A' : 'transparent',
                    borderBottom: scrolled ? '1px solid #2A2A2A' : '1px solid transparent',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '80px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template21')}
                        style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            fontSize: '28px',
                            fontWeight: 600,
                            color: '#C9A96E',
                            cursor: 'pointer',
                            letterSpacing: '4px',
                            margin: 0,
                        }}
                    >
                        NOIR
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '36px',
                        }}
                        className="noir-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    color: '#F5F0EB',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    letterSpacing: '1.5px',
                                    textTransform: 'uppercase',
                                    transition: 'color 0.3s ease',
                                }}
                                onMouseEnter={(e) => (e.target.style.color = '#C9A96E')}
                                onMouseLeave={(e) => (e.target.style.color = '#F5F0EB')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <Search
                            size={20}
                            style={{ color: '#F5F0EB', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A96E')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#F5F0EB')}
                        />
                        <User
                            size={20}
                            style={{ color: '#F5F0EB', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template21/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A96E')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#F5F0EB')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template21/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#F5F0EB', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A96E')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#F5F0EB')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#C9A96E',
                                        color: '#0A0A0A',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        fontFamily: "'DM Sans', -apple-system, sans-serif",
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="noir-mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            <Menu size={24} style={{ color: '#F5F0EB' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                    backgroundColor: '#0A0A0A',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}
            >
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <X size={28} style={{ color: '#F5F0EB' }} />
                </button>

                <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px' }}>
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontSize: '28px',
                                fontWeight: 500,
                                color: '#C9A96E',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                letterSpacing: '3px',
                                textTransform: 'uppercase',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#D4B87A')}
                            onMouseLeave={(e) => (e.target.style.color = '#C9A96E')}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Responsive Styles */}
            <style>{`
                .noir-desktop-nav {
                    display: flex !important;
                }
                .noir-mobile-menu-btn {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    .noir-desktop-nav {
                        display: none !important;
                    }
                    .noir-mobile-menu-btn {
                        display: block !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Header21;
